import { Component, Vue, Watch } from 'vue-property-decorator'
import axios from 'axios'
import { LoginStateModule } from '@/store/modules/LoginStateModule'
import DateUtils from '@/utils/DateUtils'
import DateTimeUtils  from '@/utils/DateTimeUtils'
import MenusService from '@/service/MenusService'
import CustomersService from '@/service/CustomersService'
import ProductsService from '@/service/ProductsService'
import TaxsService from '@/service/TaxsService'
import UsersService from '@/service/UsersService'
import EstimatesService from '@/service/EstimatesService'
import RequestsService from '@/service/RequestsService'
import ConfirmationsService from '@/service/ConfirmationsService'
import ReportsService from '@/service/ReportsService'
import InvoicesService from '@/service/InvoicesService'
import FileData from '@/domain/FileData'
import { DataTableOptions } from '@/domain/DataTableOptions'
import { WindowPosition } from '@/domain/WindowPosition'
import PdfService from '@/service/PdfService'
import Encoding from 'encoding-japanese'

/** アラートタイプ */
export enum ALERT_TYPE {
  SUCCESS = "success",
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
}

/** 画面モード */
export enum VIEW_MODE {
  NEW = "new",
  VIEW = "view",
  EDIT = "edit",
  DELETED = "deleted",
}

@Component
export default class GlobalMixin extends Vue {
  /* ユーティリティ */
  dateUtils = DateUtils.instance
  dateTimeUtils = DateTimeUtils.instance

  /** サービス */
  menusService = MenusService.instance
  customersService = CustomersService.instance
  productsService = ProductsService.instance
  taxsService = TaxsService.instance
  usersService = UsersService.instance
  estimatesService = EstimatesService.instance
  requestsService = RequestsService.instance
  confirmationsService = ConfirmationsService.instance
  reportsService = ReportsService.instance
  invoicesService = InvoicesService.instance
  pdfService = PdfService.instance


  /**
   * ログインユーザ名
   */
  get loginUserName() {
    return LoginStateModule.loginUserName
  }

  /**
   * ログインユーザID
   */
  get loginUserId() {
    return LoginStateModule.loginUserId
  }

  /** ローディング画面 */
  isLoading = false

  /** バリデーション */
  invalid = false

  /** 画面モード */
  viewMode = VIEW_MODE.VIEW

  /** データテーブル １ページの表示件数 */
  itemsPerPage = 50

  /** データテーブル フッター設定 */
  footerProps = {
    itemsPerPageOptions: [10, 20, 50, 100, 200, 300, 400, 500],
    showFirstLastPage: true,
    firstIcon: 'mdi-skip-backward',
    lastIcon: 'mdi-skip-forward',
    prevIcon: 'mdi-skip-previous',
    nextIcon: 'mdi-skip-next'
  }

  /** データテーブル オプション設定 */
  dataTableOptions = new DataTableOptions()

  /** 顧客選択ダイアログ */ 
  customerSelectDialog = false

  /** 代理店選択ダイアログ */ 
  agencySelectDialog = false

  /** 日付＆ユーザ選択ダイアログ */ 
  dateAndUserPickerDialog = false

  /** PDF表示ダイアログ */
  pdfViewDialog = false
  pdfViewTitle = ""
  pdfViewUrl = ""

  /** メール送信ダイアログ */
  mailSendDialog = false

  /** 一覧系リストの初期表示完了 */
  dataTableMounted = false

  /** 検査依頼種別 */
  requestTypes = [
    {code: 0, name: "一般検査"},
    {code: 1, name: "輸入検査"},
    {code: 2, name: "一般検査 日本デリカフーズ分"},
    {code: 3, name: "一般検査 横浜検査センター検査分"},
  ]

  /**
   * PDFダイアログの表示
   * @param pdfViewTitle
   * @param pdfViewUrl 
   */
  openPdfViewDialog(pdfViewTitle: string, pdfViewUrl: string) {
    this.pdfViewTitle = pdfViewTitle
    this.pdfViewUrl = pdfViewUrl
    this.pdfViewDialog = true
  }

  /**
   * ローディング画面の開始
   */
  showLoading() {
    this.isLoading = true
  }

  /**
   * ローディング画面の終了
   */
  hideLoading() {
    this.isLoading = false
  }

  /**
   * ショートカットキー（ボタン）
   */
  // eslint-disable-next-line
  shortKeyBtn(event: any) {
    if (event.srcElement === null || event.srcElement.disabled === true || event.srcElement.tagName !== "BUTTON") {
      return
    }
    event.srcElement.click()
  }

  /**
   * 新規登録か
   */
  get isNew() {
    return this.viewMode === VIEW_MODE.NEW
  }

  /**
   * 表示モードか（削除済みも表示モードとして扱う）
   */
  get isView() {
    return this.viewMode === VIEW_MODE.VIEW || this.viewMode === VIEW_MODE.DELETED
  }

  /**
   * 編集モードか
   */
  get isEdit() {
    return this.viewMode === VIEW_MODE.EDIT
  }

  /**
   * 削除済みか
   */
  get isDeleted() {
    return this.viewMode === VIEW_MODE.DELETED
  }

  /**
   * ページネーションによるページ変更
   * @param page
   */
  onPage(page: number) {
    this.dataTableOptions.page = page
  }

  /**
   * ページの変更
   */
  @Watch("dataTableOptions.itemsPerPage")
  changeDataTableOptionsItemsPerPage() {
    if (!this.dataTableMounted) return

    /* ウィンドウのトップへ */
    window.scrollTo(0, 0)
  }

  /**
   * ページの変更
   */
  @Watch("dataTableOptions.page")
  changeDataTableOptionsPage() {
    if (!this.dataTableMounted) return

    /* ウィンドウのトップへ */
    window.scrollTo(0, 0)
  }

  /**
   * 一覧系のページ状態の復元
   * @param baseKey
   */
  mountedDataTable(baseKey: string) {
    if (this.dataTableMounted) return

    const dataTableOptions = sessionStorage.getItem(`${baseKey}.dataTableOptions`)
    if (dataTableOptions === null) {
      this.dataTableMounted = true
      return
    }

    this.dataTableOptions = JSON.parse(dataTableOptions) as DataTableOptions
    sessionStorage.removeItem(`${baseKey}.dataTableOptions`)
    const page = this.dataTableOptions.page
    this.sleep(500).then(() => {
      this.dataTableOptions.page = page
      const windowPosition = sessionStorage.getItem(`${baseKey}.windowPosition`)
      if (windowPosition === null) {
        this.dataTableMounted = true
        return
      }

      const position = JSON.parse(windowPosition) as WindowPosition
      sessionStorage.removeItem(`${baseKey}.windowPosition`)
      this.sleep(500).then(() => {
        window.scrollTo(position.scrollX, position.scrollY)
        this.dataTableMounted = true
      })
    })
  }

  /** アラートメッセージ */
  alertBarType = ALERT_TYPE.INFO
  alertBarMessage = ""
  alertBarIsVisible = false

  /**
   * アラートの表示
   * @param message 
   * @param type
   */
  alert(message: string, type=ALERT_TYPE.INFO) {
    this.alertBarType = type
    this.alertBarMessage = message
    this.alertBarIsVisible = true
    window.scrollTo(0, 0)
  }

  /**
   * 確認
   * @param message
   */
  async confirm(message: string) {
    return await this.$confirm(message, {
        title: "確認",
        buttonTrueText: "OK",
        buttonFalseText: "キャンセル"
    })
  }

  /**
   * ファイルの読み込み(base64形式)
   */
  async readFileAsync(file: File): Promise<FileData | undefined> {
    return new Promise<FileData | undefined>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve({filename: file.name, content: reader.result})
        } else {
          resolve(undefined)
        }
      }
      reader.onerror = (error) => {
        reject(error)
      }
      reader.readAsDataURL(file)
    })
  }

  /**
   * URLからの読み込み(base64形式)
   * @param url 
   */
  async readUrlAsync(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      axios.get(url, {responseType: 'blob'})
          .then(response => {
            const reader = new FileReader()
            const contentType = response.headers['content-type']
            const blob = new Blob([response.data], { type: contentType })
            reader.onload = () => {
              if (typeof reader.result === "string") {
                resolve(reader.result)
              } else {
                reject("read file error")
              }
            }
            reader.onerror = (error) => {
              reject(error)
            }
            reader.readAsDataURL(blob)
          })
          .catch(error => {
            reject(error)
          })
    })
  }

  /**
   * ファイルダウンロード
   */
  download(file: FileData) {
    if (file.content !== "") {
      const a = document.createElement('a')
      a.href = file.content
      a.download = file.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
    }
  }

  /**
   * データをSJISのファイルに変換する
   */
  convertFile(fileName: string, mimeType: string, content: string) {
    const unicodeArray: number[] = []
    content.split("")
        .map(x => x.charCodeAt(0))
        .forEach(x => unicodeArray.push(x))
    const sjisArray = new Uint8Array(Encoding.convert(unicodeArray, {to: "SJIS", from: "UNICODE"}) as number[])
    return new FileData({filename: fileName, content: URL.createObjectURL(new Blob([sjisArray], { "type" : mimeType }))})
  }

  /**
   * 複数キーワード比較
   * @param srarch 
   * @param item 
   */
  filterCompare(srarch: string | null, item: string) {
    if (srarch === null) return true

    return srarch.split(/[\u{20}\u{3000}]/u)
                  .filter((x) => x !== "")
                  .every((x) => item.indexOf(x) != -1)
  }
  
  /**
   * Sleep
   * @param time
   */
  async sleep(time: number): Promise<number> {
    return new Promise<number>(resolve => {
      setTimeout(() => {
        resolve(time)
      }, time)
    })
  }
}
